import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import type {
  CopilotPageContext,
  CopilotPageHint,
} from "@/features/copilot/lib/copilot-page-context";
import { isSameCopilotPageContent } from "@/features/copilot/lib/copilot-page-context";
import { createCopilotPageHintGate } from "@/features/copilot/lib/copilot-page-hint-gate";

interface CopilotPageContextValue {
  context: CopilotPageContext | null;
  dismissed: boolean;
  dismiss: () => void;
  /** Stable getter: the hint to attach to the next initial message, or null. */
  readSendableHint: () => CopilotPageHint | null;
}

const NO_PAGE_CONTEXT: CopilotPageContextValue = {
  context: null,
  dismissed: false,
  dismiss: () => undefined,
  readSendableHint: () => null,
};

const CopilotPageContextReact = createContext<CopilotPageContextValue>(NO_PAGE_CONTEXT);

export function useCopilotPageContext(): CopilotPageContextValue {
  return useContext(CopilotPageContextReact);
}

export function CopilotPageContextProvider({
  context: derivedContext,
  children,
}: {
  context: CopilotPageContext | null;
  children: ReactNode;
}) {
  // The caller derives a fresh object every render; consumers only care when the page or what
  // it would send changes (the record name and filters arrive after the page identity).
  const previousContext = useRef(derivedContext);
  if (!isSameCopilotPageContent(previousContext.current, derivedContext)) {
    previousContext.current = derivedContext;
  }
  const context = previousContext.current;
  const [gate] = useState(createCopilotPageHintGate);
  const [, rerender] = useReducer((renders: number) => renders + 1, 0);
  const dismissed = context !== null && gate.isDismissed(context.identity);
  // Layout effect so a send that follows a navigation in the same frame sees the new page.
  useLayoutEffect(() => {
    gate.setContext(context);
  }, [gate, context]);

  const dismiss = useCallback(() => {
    if (!context) return;
    gate.dismiss(context.identity);
    rerender();
  }, [gate, context]);

  const value = useMemo(
    () => ({ context, dismissed, dismiss, readSendableHint: gate.readHint }),
    [context, dismissed, dismiss, gate.readHint],
  );
  return (
    <CopilotPageContextReact.Provider value={value}>{children}</CopilotPageContextReact.Provider>
  );
}
